/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import {
  Card,
  Box,
  Typography,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  Badge,
} from '@ublend-npm/aulaui-core';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { getStarredSpaceColors } from '../../colors';

export interface WidgetAction {
  id?: string;
  label: string;
  onClick: () => void;
}

interface WidgetCardProps {
  title: string;
  badgeCount?: number;
  badgeMessage?: string;
  children: React.ReactNode;
  headerAction?: React.ReactNode;
  titleLeading?: React.ReactNode;
  titleAdornment?: React.ReactNode;
  menuActions?: WidgetAction[];
  footer?: React.ReactNode;
  minHeight?: string | number;
  ariaLabel?: string;
  contentGap?: number;
  borderRadius?: string | number;
}

const WidgetCard: React.FC<WidgetCardProps> = ({
  title,
  badgeCount,
  badgeMessage,
  children,
  headerAction,
  titleLeading,
  titleAdornment,
  menuActions,
  footer,
  minHeight = '560px',
  ariaLabel,
  contentGap = 1,
  borderRadius = '24px',
}) => {
  const { palette } = useTheme();
  const colors = getStarredSpaceColors(palette);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleClickMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <Card
      aria-label={ariaLabel}
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        borderRadius,
        padding: '16px',
        boxShadow: 'none',
        border: `1px solid ${palette.divider}`,
        bgcolor: palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
        mt={-0.5}
      >
        <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
          {titleLeading}
          <Typography
            variant="subtitle1"
            component="h2"
            fontWeight="medium"
            color="primary"
          >
            {title}{' '}
            {badgeCount ? (
              <Badge
                badgeContent={badgeCount}
                role="status"
                aria-live="polite"
                aria-label={badgeMessage}
                color="primary"
                sx={{ marginLeft: 2 }}
              />
            ) : null}
          </Typography>
          {titleAdornment}
        </Box>
        <Box display="flex" alignItems="center">
          {headerAction && <Box>{headerAction}</Box>}
          {menuActions && menuActions.length > 0 && (
            <Box ml={headerAction ? 1 : 0}>
              <IconButton
                aria-label={`${title} options`}
                id={`widget-button-${title.replace(/\s+/g, '-').toLowerCase()}`}
                aria-controls={
                  isMenuOpen
                    ? `widget-menu-${title.replace(/\s+/g, '-').toLowerCase()}`
                    : undefined
                }
                aria-expanded={isMenuOpen ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleClickMenu}
                size="small"
                sx={{
                  color: colors.widgetMenuIcon,
                }}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                id={`widget-menu-${title.replace(/\s+/g, '-').toLowerCase()}`}
                MenuListProps={{
                  'aria-labelledby': `widget-button-${title
                    .replace(/\s+/g, '-')
                    .toLowerCase()}`,
                }}
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleCloseMenu}
                slotProps={{
                  paper: {
                    sx: {
                      minWidth: 120,
                    },
                  },
                }}
              >
                {menuActions.map((action, idx) => (
                  <MenuItem
                    key={action.id || action.label || `action-${idx}`}
                    label={action.label}
                    onClick={() => {
                      handleCloseMenu();
                      action.onClick();
                    }}
                  />
                ))}
              </Menu>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        gap={contentGap}
        flex={1}
        sx={{
          minWidth: 0,
          width: '100%',
          minHeight: 0,
        }}
      >
        {children}
      </Box>

      {footer && <Box mt={2}>{footer}</Box>}
    </Card>
  );
};

export default WidgetCard;
